// The bell's list: the signed-in user's own notifications plus the town-wide pushes of the last
// seven days (rivers, municipal notices, new incidencias and help requests), which everyone sees,
// signed in or not, on the web or in the app. The broadcast rows are shared, so whether one has
// been read is kept on this device (localStorage); personal items are marked read on the server.

export const READ_KEY = 'somostorre:bell-read';
const MAX_READ = 300;

// Wrapped: storage can be missing or throw (private mode, blocked site data, previews).
export function loadReadIds(storage = typeof window !== 'undefined' ? window.localStorage : null) {
  try {
    const raw = storage?.getItem(READ_KEY);
    const ids = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(ids) ? ids.filter((x) => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

export function saveReadIds(ids, storage = typeof window !== 'undefined' ? window.localStorage : null) {
  try {
    // Newest last; only the most recent are kept (the list itself only goes back 7 days).
    storage?.setItem(READ_KEY, JSON.stringify([...ids].slice(-MAX_READ)));
  } catch { /* storage unavailable: read state lasts for this visit only */ }
}

const time = (iso) => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isNaN(t) ? 0 : t;
};

// One list, newest first. Personal items keep `read` from the server; broadcasts are read when
// their id is in readIds.
export function mergeInbox(personal = [], broadcasts = [], readIds = new Set()) {
  const mine = (personal || []).map((n) => ({
    key: `p:${n.sent_at}`,
    kind: 'personal',
    id: n.sent_at,
    category: n.category,
    title: n.title || '',
    body: n.body || '',
    deepLink: n.data?.deepLink || null,
    sent_at: n.sent_at,
    read: !!n.read,
  }));
  const town = (broadcasts || []).map((b) => ({
    key: `b:${b.id}`,
    kind: 'broadcast',
    id: b.id,
    category: b.category,
    title: b.title || '',
    body: b.body || '',
    deepLink: b.deepLink || null,
    sent_at: b.sent_at,
    read: readIds.has(b.id),
  }));
  return [...mine, ...town].sort((a, b) => time(b.sent_at) - time(a.sent_at));
}

export function unreadBroadcasts(broadcasts = [], readIds = new Set()) {
  return (broadcasts || []).filter((b) => !readIds.has(b.id)).length;
}

// The item marked read, and the new set of read broadcast ids (unchanged for personal items).
export function markRead(items, item, readIds) {
  const next = new Set(readIds);
  if (item.kind === 'broadcast') {
    next.delete(item.id);
    next.add(item.id);
  }
  return { items: items.map((i) => (i.key === item.key ? { ...i, read: true } : i)), readIds: next };
}

export function markAll(items, readIds) {
  const next = new Set(readIds);
  items.filter((i) => i.kind === 'broadcast').forEach((i) => next.add(i.id));
  return { items: items.map((i) => ({ ...i, read: true })), readIds: next };
}

// Only paths inside the app are followed (the list is built from our own pushes, but a link that
// is not a path would leave the site).
export function safeLink(link) {
  return typeof link === 'string' && link.startsWith('/') && !link.startsWith('//') ? link : null;
}

export function timeAgo(iso, now = Date.now()) {
  const t = time(iso);
  if (!t) return '';
  const diff = (now - t) / 1000;
  if (diff < 60) return 'ahora';
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  const d = Math.floor(diff / 86400);
  return d === 1 ? 'ayer' : `hace ${d} días`;
}
